import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import { motion } from 'framer-motion';
import { useState } from 'react';

import {
  CardContainer,
  CardHeaderRow,
  FieldList,
  FieldRowItem,
  FieldRowLabel,
  FieldRowValue,
  ReportFieldRow,
  ReportTitle,
  SectionDivider,
  SeeFullReportCTA,
  SubsectionTitle,
  ViewReportButton,
} from '../../components';
import { calculateAge, formatDob, formatDod } from '../utils';

import {
  DateLine,
  LinkText,
  LocationText,
  MessageText,
  NameInfo,
  NameSection,
  PhotoContainer,
} from './components';

import type { ObituaryReportData } from 'components/DeepSearch/types';

const MAX_MESSAGE_LENGTH = 280;

function toSafeExternalUrl(raw: string): string | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

function normalizeMessageBody(message: string): string {
  const firstLine = message.split('\n')[0] ?? '';
  return firstLine.trim() === ''
    ? message.split('\n').slice(1).join('\n').trim()
    : message.trim();
}

function truncateMessage(message: string): {
  preview: string;
  truncated: boolean;
} {
  const body = normalizeMessageBody(message);
  if (body.length <= MAX_MESSAGE_LENGTH) {
    return { preview: body, truncated: false };
  }
  return {
    preview: `${body.slice(0, MAX_MESSAGE_LENGTH).trimEnd()}...`,
    truncated: true,
  };
}

interface ObituaryReportPreviewCardProps {
  data: ObituaryReportData;
  onViewReport?: () => void;
}

export const ObituaryReportPreviewCard = ({
  data,
  onViewReport,
}: ObituaryReportPreviewCardProps) => {
  const [showFullMessage, setShowFullMessage] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const age = calculateAge(data.dob, data.dod);
  const { preview, truncated } = truncateMessage(data.message);
  const body = normalizeMessageBody(data.message);
  const safeFuneralHomeUrl = toSafeExternalUrl(data.funeralHomeUrl);
  const safeObituaryUrl = toSafeExternalUrl(data.obituaryUrl);

  const dateParts = [
    data.dob ? `Date of Birth: ${formatDob(data.dob)}` : null,
    data.dod ? `Date of Death: ${formatDod(data.dod)}` : null,
    age != null ? `Age: ${age}` : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <CardContainer>
      <CardHeaderRow sx={{ mb: 1 }}>
        <LocalFloristIcon sx={{ fontSize: 24 }} />
        <ReportTitle m={0}>{data.name || 'Obituary Report'}</ReportTitle>
        {onViewReport && <ViewReportButton onClick={onViewReport} />}
      </CardHeaderRow>

      <NameSection>
        <PhotoContainer>
          {data.photoUrl && !photoError ? (
            <img
              src={data.photoUrl}
              alt={data.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setPhotoError(true)}
            />
          ) : (
            'PHOTO'
          )}
        </PhotoContainer>
        <NameInfo>
          {dateParts && <DateLine variant="body2">{dateParts}</DateLine>}
          {data.location && (
            <LocationText variant="body2">{data.location}</LocationText>
          )}
        </NameInfo>
      </NameSection>

      <SectionDivider />

      <FieldList>
        {data.funeralHome && (
          <FieldRowItem>
            <FieldRowLabel>Funeral Home:</FieldRowLabel>
            <FieldRowValue>
              {safeFuneralHomeUrl ? (
                <LinkText
                  href={safeFuneralHomeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.funeralHome}
                </LinkText>
              ) : (
                data.funeralHome
              )}
            </FieldRowValue>
          </FieldRowItem>
        )}
        {data.funeralHomeAddress && (
          <ReportFieldRow label="Address" value={data.funeralHomeAddress} />
        )}
        {safeObituaryUrl && (
          <FieldRowItem>
            <FieldRowLabel>Source:</FieldRowLabel>
            <FieldRowValue>
              <LinkText
                href={safeObituaryUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View full obituary
              </LinkText>
            </FieldRowValue>
          </FieldRowItem>
        )}
      </FieldList>

      {data.message && (
        <>
          <SectionDivider />
          <SubsectionTitle>Obituary</SubsectionTitle>
          <motion.div
            key={showFullMessage ? 'full' : 'preview'}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <MessageText variant="body2">
              {showFullMessage ? body : preview}
            </MessageText>
          </motion.div>
          {truncated && (
            <LinkText
              as="button"
              onClick={() => setShowFullMessage((prev) => !prev)}
            >
              {showFullMessage ? 'Show less' : 'Read full obituary'}
            </LinkText>
          )}
        </>
      )}
      {onViewReport && <SeeFullReportCTA onClick={onViewReport} />}
    </CardContainer>
  );
};
